import { useRef, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import type { PlanungOrderLink } from "@/lib/montageplanung";
import {
  FinePlanningPanel,
  type FinePlanningPanelHandle,
  type FinePlanningPanelState,
} from "./FinePlanningPanel";

export interface FinePlanningDialogProps {
  open: boolean;
  onClose: () => void;
  assignmentId: number;
  assignmentTitle: string;
  /** Gespeicherter Zeitraum – bei Abweichung vom Formular Hinweis anzeigen. */
  savedDateFrom?: string;
  savedDateTo?: string;
  onSaved?: (assignmentOrders?: PlanungOrderLink[]) => void;
}

export function FinePlanningDialog({
  open,
  onClose,
  assignmentId,
  assignmentTitle,
  savedDateFrom,
  savedDateTo,
  onSaved,
}: FinePlanningDialogProps) {
  const panelRef = useRef<FinePlanningPanelHandle>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panelState, setPanelState] = useState<FinePlanningPanelState>({
    dirty: false,
    loading: false,
    canEdit: false,
    datesOutOfSync: false,
  });

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const orders = await panelRef.current?.save(
        savedDateFrom && savedDateTo
          ? { dateFrom: savedDateFrom, dateTo: savedDateTo }
          : undefined,
      );
      onSaved?.(orders ?? undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={panelState.dirty}
      title="Feinplanung"
      description={assignmentTitle}
      size="xl"
      fullScreenMobile
      zIndexClass="z-[210]"
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={saving} />
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            loading={saving}
            disabled={panelState.loading || panelState.datesOutOfSync || !panelState.canEdit}
          >
            Speichern
          </Button>
        </>
      }
    >
      {error && <p className="mb-3 text-sm text-status-red">{error}</p>}
      <FinePlanningPanel
        ref={panelRef}
        assignmentId={assignmentId}
        active={open}
        savedDateFrom={savedDateFrom}
        savedDateTo={savedDateTo}
        onStateChange={setPanelState}
      />
    </Dialog>
  );
}
